/** Account settings for clinician/admin: identity + self password change. */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../lib/auth.js';
import { useI18n } from '../lib/i18n.js';
import { ApiError } from '../lib/api.js';
import { Card, Button, Field, inputClass } from '../components/ui.js';

export function Settings() {
  const { user, changePassword } = useAuth();
  const { t } = useI18n();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [clientError, setClientError] = useState('');
  const [done, setDone] = useState(false);

  const m = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: () => {
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setClientError('');
    setDone(false);
    if (next !== confirm) {
      setClientError(t('auth.passwordMismatch'));
      return;
    }
    if (next.length < 12) {
      setClientError(t('auth.passwordTooShort'));
      return;
    }
    m.mutate();
  }

  // Map server errors to friendly messages.
  function serverError(): string {
    const err = m.error;
    if (err instanceof ApiError) {
      if (err.status === 401) return t('auth.currentPasswordWrong');
      if (err.status === 400 && /differ/i.test(err.message)) return t('auth.passwordSame');
    }
    return t('common.error');
  }

  return (
    <div>
      <Card title={t('account.title')}>
        <p className="text-sm text-slate-600">{t('account.signedInAs', { name: user?.name ?? '' })}</p>
      </Card>

      <Card title={t('auth.changePassword')}>
        <form onSubmit={submit} className="max-w-sm">
          <Field label={t('auth.currentPassword')}>
            <input
              type="password"
              autoComplete="current-password"
              className={inputClass}
              value={current}
              onChange={(e) => { setCurrent(e.target.value); setDone(false); }}
              required
            />
          </Field>
          <Field label={t('auth.newPassword')}>
            <input
              type="password"
              minLength={12}
              autoComplete="new-password"
              className={inputClass}
              value={next}
              onChange={(e) => { setNext(e.target.value); setDone(false); }}
              required
            />
          </Field>
          <Field label={t('auth.confirmPassword')}>
            <input
              type="password"
              minLength={12}
              autoComplete="new-password"
              className={inputClass}
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setDone(false); }}
              required
            />
          </Field>

          {clientError && <p className="text-danger text-sm mb-3">{clientError}</p>}
          {m.isError && <p className="text-danger text-sm mb-3">{serverError()}</p>}
          {done && <p className="text-green-700 text-sm mb-3">{t('auth.passwordChanged')}</p>}

          <Button type="submit" disabled={m.isPending}>
            {m.isPending ? '…' : t('auth.changePassword')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
